/**
 * LicenseEntryScreen — Pantalla de ingreso de clave cuando:
 *  1. No hay licencia registrada (instalación nueva)
 *  2. La clave almacenada fue rechazada explícitamente por ByteControl (mode: no_license)
 *
 * El usuario puede seguir logueado. Solo ve esta pantalla en lugar del UI principal.
 * Al activar una clave válida, el Layout detecta mode=active y muestra el UI normal.
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { KeyRound, LogOut, RefreshCw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/lib/auth";
import { apiCall } from "@/lib/api";
import { toast } from "sonner";

type LicenseStatus = {
  enabled: boolean;
  mode: string;
  message: string;
  current: { license_key: string } | null;
};

export function LicenseEntryScreen() {
  const logout = useAuthStore((s) => s.logout);
  const qc = useQueryClient();
  const [key, setKey] = useState("");
  const [activating, setActivating] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const { data: status } = useQuery<LicenseStatus>({
    queryKey: ["licensing-status"],
    queryFn: () => apiCall<LicenseStatus>("get", "/api/v1/licensing/status"),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  // Hay una clave guardada pero fue rechazada (rejected) vs instalación sin licencia
  const hadLicense = !!status?.current;

  const heartbeatMut = useMutation({
    mutationFn: () => apiCall("post", "/api/v1/licensing/heartbeat"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["licensing-status"] });
      toast.success("Verificación enviada — recargando estado…");
    },
    onError: () => {
      toast.error("No se pudo verificar con ByteControl");
    },
  });

  const handleActivate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!key.trim()) return;
    setErr(null);
    setActivating(true);
    try {
      await apiCall("post", "/api/v1/licensing/activate", { json: { license_key: key.trim() } });
      toast.success("Licencia activada correctamente");
      qc.invalidateQueries({ queryKey: ["licensing-status"] });
    } catch (e: any) {
      const msg =
        e?.detail?.error?.message ||
        e?.error?.message ||
        e?.message ||
        "Clave inválida o rechazada por ByteControl";
      setErr(msg);
    } finally {
      setActivating(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-6">
      <div className="flex flex-col items-center gap-6 max-w-md w-full text-center">
        {/* Icono */}
        <div className="rounded-full bg-yellow-100 dark:bg-yellow-900/30 p-5">
          <KeyRound size={48} className="text-yellow-500" />
        </div>

        {/* Título y descripción */}
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-bold tracking-tight">
            {hadLicense ? "Licencia inválida" : "Licencia requerida"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {hadLicense
              ? "La clave de licencia registrada fue rechazada por ByteControl. Ingresa una clave válida para continuar."
              : "Esta instalación no tiene licencia registrada. Ingresa tu clave de ByteISP para activar el sistema."}
          </p>
        </div>

        {/* Formulario de activación */}
        <form onSubmit={handleActivate} className="w-full flex flex-col gap-3">
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="BC-XXXX-XXXX-XXXX-XXXX"
              value={key}
              onChange={(e) => { setKey(e.target.value); setErr(null); }}
              disabled={activating}
              className="flex-1 font-mono text-sm bg-background border border-input rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <Button type="submit" disabled={activating || !key.trim()}>
              {activating && <Loader2 size={14} className="animate-spin mr-2" />}
              Activar
            </Button>
          </div>
          {err && (
            <div className="text-xs text-red-500 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded px-3 py-2 text-left">
              {err}
            </div>
          )}
        </form>

        {/* Info de contexto */}
        <div className="rounded-lg border border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20 px-4 py-3 text-xs text-yellow-800 dark:text-yellow-300 text-left w-full">
          <p className="font-medium mb-1">¿Cómo obtengo mi clave?</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Tu proveedor de ByteISP te la proporciona al contratar el servicio.</li>
            <li>Formato: <span className="font-mono">BC-XXXX-XXXX-XXXX-XXXX</span></li>
            {hadLicense && <li>Si acabas de renovar o cambiar de plan, ingresa la nueva clave aquí.</li>}
          </ul>
        </div>

        {/* Botones secundarios */}
        <div className="flex gap-3 w-full">
          {hadLicense && (
            <Button
              variant="outline"
              className="flex-1"
              disabled={heartbeatMut.isPending}
              onClick={() => heartbeatMut.mutate()}
            >
              {heartbeatMut.isPending
                ? <Loader2 size={14} className="animate-spin mr-2" />
                : <RefreshCw size={14} className="mr-2" />
              }
              Verificar clave actual
            </Button>
          )}
          <Button
            variant="destructive"
            className={hadLicense ? "flex-1" : "w-full"}
            onClick={() => { logout(); window.location.href = "/login"; }}
          >
            <LogOut size={14} className="mr-2" />
            Cerrar sesión
          </Button>
        </div>

        <p className="text-xs text-muted-foreground">
          ByteISP — Contacta a tu proveedor para obtener tu clave de licencia
        </p>
      </div>
    </div>
  );
}
